import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import {
  QrCodeScanner,
  Search,
  Assignment,
  Inventory,
  Factory,
  LocationOn,
  Person,
  PlayArrow,
  Edit,
  Visibility,
} from '@mui/icons-material';
import { QREntity, MOCK_ENTITIES } from './QRGenerator';

const ALL_ENTITIES: QREntity[] = Object.values(MOCK_ENTITIES).flat();

const getEntityIcon = (type: QREntity['type']) => {
  switch (type) {
    case 'work_order': return <Assignment color="primary" />;
    case 'material_lot': return <Inventory color="success" />;
    case 'work_center': return <Factory color="warning" />;
    case 'location': return <LocationOn color="info" />;
    case 'employee': return <Person color="secondary" />;
    default: return <Assignment />;
  }
};

const getQuickActions = (entity: QREntity): { label: string; icon: React.ReactNode; color: 'primary' | 'success' | 'warning' | 'info' }[] => {
  switch (entity.type) {
    case 'work_order':
      return [
        { label: 'Start Operation', icon: <PlayArrow />, color: 'success' },
        { label: 'View Details', icon: <Visibility />, color: 'primary' },
        { label: 'Record Production', icon: <Edit />, color: 'info' },
      ];
    case 'material_lot':
      return [
        { label: 'View Inventory', icon: <Visibility />, color: 'primary' },
        { label: 'Record Consumption', icon: <Edit />, color: 'warning' },
      ];
    case 'work_center':
      return [
        { label: 'View Status', icon: <Visibility />, color: 'primary' },
        { label: 'View Queue', icon: <Assignment />, color: 'info' },
      ];
    case 'location':
      return [
        { label: 'View Contents', icon: <Visibility />, color: 'primary' },
      ];
    case 'employee':
      return [
        { label: 'View Profile', icon: <Visibility />, color: 'primary' },
        { label: 'Assign to Station', icon: <Factory />, color: 'info' },
      ];
    default:
      return [];
  }
};

const QRScanner: React.FC = () => {
  const [manualCode, setManualCode] = useState('');
  const [foundEntity, setFoundEntity] = useState<QREntity | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [recentScans, setRecentScans] = useState<QREntity[]>([]);

  const handleLookup = async () => {
    if (!manualCode.trim()) return;
    setIsSearching(true);
    setNotFound(false);
    setFoundEntity(null);

    // Simulate API lookup delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    const match = ALL_ENTITIES.find(
      (e) => e.code.toLowerCase() === manualCode.trim().toLowerCase()
    );

    if (match) {
      setFoundEntity(match);
      setRecentScans((prev) => {
        const filtered = prev.filter((e) => e.id !== match.id);
        return [match, ...filtered].slice(0, 5);
      });
    } else {
      setNotFound(true);
    }
    setIsSearching(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLookup();
  };

  const handleSelectRecent = (entity: QREntity) => {
    setFoundEntity(entity);
    setManualCode(entity.code);
    setNotFound(false);
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="medium">
          QR Scanning on Desktop
        </Typography>
        <Typography variant="body2">
          QR camera scanning is available on the MES Mobile App. On desktop, use the manual code
          lookup below to validate any QR code by entering its entity code.
        </Typography>
      </Alert>

      <Box display="flex" gap={3} flexWrap="wrap">
        {/* Left: Manual Lookup */}
        <Box flex={1} minWidth={280}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <QrCodeScanner color="primary" />
                <Typography variant="h6">Manual Code Lookup</Typography>
              </Box>

              <Box display="flex" gap={1} mb={2}>
                <TextField
                  label="Enter Entity Code"
                  value={manualCode}
                  onChange={(e) => { setManualCode(e.target.value); setNotFound(false); }}
                  onKeyDown={handleKeyDown}
                  size="small"
                  placeholder="e.g. WO-2024-001"
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handleLookup}
                  disabled={!manualCode.trim() || isSearching}
                  startIcon={isSearching ? <CircularProgress size={16} color="inherit" /> : <Search />}
                  sx={{ minWidth: 100 }}
                >
                  Lookup
                </Button>
              </Box>

              {notFound && (
                <Alert severity="warning">
                  No entity found with code "{manualCode}". Check the code and try again.
                </Alert>
              )}

              {/* Found Entity */}
              {foundEntity && (
                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {getEntityIcon(foundEntity.type)}
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {foundEntity.code}
                      </Typography>
                      <Chip
                        label={foundEntity.type.replace(/_/g, ' ').toUpperCase()}
                        size="small"
                        color="primary"
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" mb={1}>
                    {foundEntity.name}
                  </Typography>

                  {foundEntity.metadata && (
                    <Box mb={2}>
                      {Object.entries(foundEntity.metadata).map(([k, v]) => (
                        <Typography key={k} variant="caption" display="block" color="textSecondary">
                          {k}: {v}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                    Quick Actions:
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {getQuickActions(foundEntity).map((action) => (
                      <Button
                        key={action.label}
                        size="small"
                        variant="outlined"
                        color={action.color}
                        startIcon={action.icon}
                        onClick={() => {
                          // Actions would navigate to relevant pages in production
                          alert(`Action: ${action.label} for ${foundEntity.code}`);
                        }}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Box>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Right: Recent Scans */}
        <Box minWidth={240} maxWidth={320}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Lookups
              </Typography>

              {recentScans.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No recent lookups. Search for an entity code above.
                </Typography>
              ) : (
                <List disablePadding>
                  {recentScans.map((entity, index) => (
                    <React.Fragment key={entity.id}>
                      {index > 0 && <Divider />}
                      <ListItem
                        button
                        onClick={() => handleSelectRecent(entity)}
                        sx={{ px: 0.5, py: 0.75 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {getEntityIcon(entity.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight="medium">
                              {entity.code}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="textSecondary">
                              {entity.name}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Try These Example Codes:
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.5}>
                {[
                  'WO-2024-001',
                  'LOT-RM001-001',
                  'WC-001',
                  'AREA-A',
                  'EMP-001',
                ].map((code) => (
                  <Button
                    key={code}
                    size="small"
                    variant="text"
                    onClick={() => { setManualCode(code); }}
                    sx={{ justifyContent: 'flex-start', fontFamily: 'monospace', fontSize: '0.75rem' }}
                  >
                    {code}
                  </Button>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default QRScanner;
